import React, { useEffect, useState } from "react";
import {
  STAGE_CONFIGS,
  StageConfig,
} from "../../../../constants/stageConstants";
import { useAppSelector } from "../../../../store";
import CustomButton from "../../../UI/Buttons/CustomButton";
import LeftArrow from "../../../UI/Icons/LeftArrow";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import CheckboxInput from "../../../UI/Inputs/Checkbox";
import RadioInput from "../../../UI/Inputs/RadioInput";
import PopupModal from "../../../UI/Popup";

export interface ReopenStageModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (payload: {
    candidate_id: number;
    stage_number: number;
    notify_employee: boolean;
    reopened_sections?: string[];
    reason?: string;
  }) => void;
  stageNumber: number;
  candidateId?: number;
  candidateName?: string;
  candidatePosition?: string;
  candidateAvatar?: string;
  error?: string | null;
  isLoading?: boolean;
}

const ReopenStageModal: React.FC<ReopenStageModalProps> = ({
  visible,
  onClose,
  onConfirm,
  stageNumber,
  candidateId,
  candidateName,
  candidatePosition,
  candidateAvatar,
  error,
}) => {
  const stageConfig: StageConfig | undefined = STAGE_CONFIGS[stageNumber];
  const isLoading = useAppSelector((state) => state.superadmin.inProgress);

  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, boolean | string>
  >({});
  const [radioSelection, setRadioSelection] = useState<string>("");
  const isCConnectIDVerified = useAppSelector((state) => state.superadmin.reviewCandidateData?.is_connectid_verified);

  useEffect(() => {
    // Reset selections when modal opens or stage changes
    if (visible) {
      const initialState: Record<string, boolean | string> = {};

      // Set mandatory checkboxes to true by default
      if (stageConfig?.notificationOption?.mandatory) {
        initialState[stageConfig.notificationOption.id] = true;
      }

      // For stage 2, initialize with one radio option selected by default
      if (stageNumber === 2 && stageConfig?.reopenOptions.length > 0) {
        const defaultRadio =
          stageConfig.reopenOptions[1]?.id || stageConfig.reopenOptions[0]?.id;
        setRadioSelection(defaultRadio);
        initialState[defaultRadio] = true;
      }

      setSelectedOptions(initialState);
    }
  }, [visible, stageNumber, stageConfig]);

  const handleCheckboxChange = (optionId: string, checked: boolean) => {
    setSelectedOptions((prev) => {
      const updatedOptions = {
        ...prev,
        [optionId]: checked,
      };

      // Stage 1: Auto-check/uncheck notify_employee based on reopen checkboxes
      if (stageNumber === 1) {
        if (optionId === 'reopen_id_verification' || optionId === 'reopen_online_interview') {
          const isAnyReopenChecked =
            updatedOptions['reopen_id_verification'] ||
            updatedOptions['reopen_online_interview'];

          updatedOptions['notify_employee'] = !!isAnyReopenChecked;
        }
      }

      // Stage 3: Auto-check/uncheck notify_employee based on reopen checkboxes
      if (stageNumber === 3) {
        if (
          optionId === 'reopen_tfn_declaration' ||
          optionId === 'reopen_superannuation' ||
          optionId === 'reopen_contract'
        ) {
          const isAnyReopenChecked =
            updatedOptions['reopen_tfn_declaration'] ||
            updatedOptions['reopen_superannuation'] ||
            updatedOptions['reopen_contract'];

          updatedOptions['notify_employee'] = !!isAnyReopenChecked;
        }
      }

      return updatedOptions;
    });
  };

  const handleRadioChange = (optionId: string) => {
    setRadioSelection(optionId);

    // Clear all radio options first
    const clearedRadios = { ...selectedOptions };
    stageConfig?.reopenOptions.forEach((option) => {
      if (option.type === "radio") {
        clearedRadios[option.id] = false;
      }
    });

    // Set the selected radio
    setSelectedOptions({
      ...clearedRadios,
      [optionId]: true,
    });
  };

  const handleConfirm = () => {
    if (!candidateId) {
      console.error("Candidate ID is required to reopen stage");
      return;
    }

    // Transform selected options into API payload format
    const payload: {
      candidate_id: number;
      stage_number: number;
      notify_employee: boolean;
      reopened_sections?: string[];
      reason?: string;
    } = {
      candidate_id: candidateId,
      stage_number: stageNumber,
      notify_employee: false,
    };

    // Determine notify_employee based on stage and selections
    if (stageNumber === 1 || stageNumber === 3) {
      // For stage 1 & 3, check if notify_employee checkbox is selected
      payload.notify_employee = !!selectedOptions["notify_employee"];
    } else if (stageNumber === 2) {
      // For stage 2, use radio selection to determine notify_employee
      payload.notify_employee =
        selectedOptions["notify_employee_referoo"] === true;
    }

    // Map reopened sections based on stage
    const reopenedSections: string[] = [];

    if (stageNumber === 1) {
      // Map stage 1 specific section IDs to API format
      if (selectedOptions["reopen_id_verification"]) {
        reopenedSections.push("id_verification");
      }
      if (selectedOptions["reopen_online_interview"]) {
        reopenedSections.push("online_interview");
      }

     
      reopenedSections.push("personal_details");
      reopenedSections.push("qualification");
      reopenedSections.push("jobpreference");
      
    } else if (stageNumber === 3) {
      // Map stage 3 specific section IDs to API format
      if (selectedOptions["reopen_tfn_declaration"]) {
        reopenedSections.push("tfn_form");
      }
      if (selectedOptions["reopen_superannuation"]) {
        reopenedSections.push("superannuation_form");
      }
      if (selectedOptions["reopen_contract"]) {
        reopenedSections.push("contract_form");
      }
      reopenedSections.push("casual_fair_work_statement");
      reopenedSections.push("bankAccount");
      reopenedSections.push("emergency_info");
    } else if (stageNumber === 2) {
      // Map stage 2 specific section IDs to API format
      if (selectedOptions["admin_complete"]) {
        reopenedSections.push("admin_complete");
      } else if (selectedOptions["notify_employee_referoo"]) {
        reopenedSections.push("candidate_complete");
      }
    }

    // Only add reopened_sections if there are any
    if (reopenedSections.length > 0) {
      payload.reopened_sections = reopenedSections;
    }

    onConfirm(payload);
  };

  const isConfirmDisabled = () => {
    // For stage 2, ensure a radio option is selected
    if (stageNumber === 2) {
      return !radioSelection;
    }

    // For stages 1 and 3, ensure at least one checkbox is selected
    // User can select only notify_employee or at least one reopen checkbox
    if (stageNumber === 1) {
      return !(
        selectedOptions["reopen_id_verification"] ||
        selectedOptions["reopen_online_interview"] ||
        selectedOptions["notify_employee"]
      );
    }

    if (stageNumber === 3) {
      return !(
        selectedOptions["reopen_tfn_declaration"] ||
        selectedOptions["reopen_superannuation"] ||
        selectedOptions["reopen_contract"] ||
        selectedOptions["notify_employee"]
      );
    }

    return false;
  };

  const isNotifyEmployeeDisabled = () => {
    // For Stage 1: Disable notify_employee if either reopen checkbox is checked
    if (stageNumber === 1) {
      return !!(
        selectedOptions["reopen_id_verification"] ||
        selectedOptions["reopen_online_interview"]
      );
    }
    // For Stage 3: Disable notify_employee if any reopen checkbox is checked
    if (stageNumber === 3) {
      return !!(
        selectedOptions["reopen_tfn_declaration"] ||
        selectedOptions["reopen_superannuation"] ||
        selectedOptions["reopen_contract"]
      );
    }
    return false;
  };

  if (!stageConfig) {
    return null;
  }

  console.log("isCConnectIDVerified", isCConnectIDVerified)

  return (
    <PopupModal
      visible={visible}
      showCloseBtn={false}
      onClickClose={onClose}
      customWidth={650}
    >
      <div className="d-flex flex-column gap-20">

        <div className="d-flex flex-column gap-10">
          <div className="fs-22 fw-400 text-black">Reopen Stage</div>
          <div className="bg-off-white h-1px w-100"></div>
        </div>
        <div className="d-flex flex-column flex-md-row gap-20 align-items-md-center justify-content-md-between">
          {/* Candidate Info */}
          {candidateName && (
            <div className="d-flex gap-20 align-items-center">
              <div
                className="rounded-circle overflow-hidden"
                style={{ minWidth: 48, height: 48 }}
              >
                {candidateAvatar ? (
                  <img
                    src={candidateAvatar}
                    alt={candidateName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div className="bg-teal-muted w-100 h-100 d-flex align-items-center justify-content-center text-white fs-18 fw-600">
                    {candidateName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="d-flex flex-column">
                <div className="fs-16 fw-700 text-black">{candidateName}</div>
                {candidatePosition && (
                  <div className="fs-13 fw-400 text-grey-black">
                    {candidatePosition}
                  </div>
                )}
              </div>
            </div>
          )}

          <CustomButton
            type="button"
            variant="outline"
            onClick={onClose}
            className="btn-theme text-grey-black bg-off-white text-nowrap"
          >
            <LeftArrow />
            Back to Profile
          </CustomButton>
        </div>
        {/* Stage Title & Description */}
        <div className="d-flex flex-column gap-10">
          <div className="fs-16 fw-700 text-black">
            Would you like to reopen Stage {stageNumber}: {stageConfig.title}?
          </div>
          <div className="fs-14 fw-400 text-black">
            {stageConfig.description}
          </div>
        </div>

        {/* Options */}
        <div className="d-flex flex-column gap-12">
          {stageConfig.reopenOptions.map((option) => (
            option.id === "reopen_id_verification" && isCConnectIDVerified === true ? null : (
              <div key={option.id} className="d-flex align-items-center gap-8 theme-check">
                {option.type === "checkbox" ? (
                  <CheckboxInput
                    id={option.id}
                    label={option.label}
                    checked={!!selectedOptions[option.id]}
                    onChange={(checked) =>
                      handleCheckboxChange(option.id, checked)
                    }
                    themeCheck={true}
                  />
                ) : (
                  <RadioInput
                    name={option.name || "reopen_option"}
                    label={option.label}
                    value={option.id}
                    checked={radioSelection === option.id}
                    onChange={() => handleRadioChange(option.id)}
                    themeCheck={true}
                  />
                )}
              </div>
            )
          ))}

          {/* Notification Option */}
          {stageConfig.notificationOption && (
            <div className="d-flex align-items-center gap-8 theme-check">
              <CheckboxInput
                id={stageConfig.notificationOption.id}
                label={`${stageConfig.notificationOption.label}${
                  stageConfig.notificationOption.mandatory ? " (Mandatory)" : ""
                }`}
                checked={!!selectedOptions[stageConfig.notificationOption.id]}
                onChange={(checked) =>
                  handleCheckboxChange(
                    stageConfig.notificationOption!.id,
                    checked,
                  )
                }
                disabled={isNotifyEmployeeDisabled()}
                themeCheck={true}
              />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="d-flex gap-12 justify-content-end">
          <CustomButton
            type="button"
            variant="primary"
            onClick={handleConfirm}
            disabled={isConfirmDisabled()}
            isLoading={isLoading}
            className={`btn-theme mobile-w-100 ${
              isConfirmDisabled()
                ? "text-grey-medium bg-off-white"
                : "bg-teal text-black"
            }`}
          >
            <span className="d-flex align-items-center gap-8">
              <RoundedTick color={isConfirmDisabled() ? "#aaa" : "#000"} />
              Confirm and Reopen
            </span>
          </CustomButton>
        </div>
        {error && (
          <div className="fs-13 fw-400 text-danger">{error}</div>
        )}
      </div>
    </PopupModal>
  );
};

export default ReopenStageModal;
